/// <amd-module name="Test/Analytics/TestResultsGrid/Source/DetailedTestListDefaultSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import { isShowFlakyTestsDetailsEnabled } from "Test/Analytics/Common/Utils/FeatureFlagUtils";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";

export class DetailedTestListDefaultSource extends TestReportSource {
    public static readonly ODataNextLink: string = "@odata.nextLink";
    public static readonly Value: string = "value";

    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_DetailedTestListDefaultReport", pageContext);
    }

    public async queryDefaultDetailedList(
        testResultContext: CommonTypes.ITestReportContext,
        nextPageToken: CommonTypes.INextDataPageToken | null | undefined
    ): Promise<CommonTypes.IDetailedListData> {
        let responsePromise: Promise<CommonTypes.IODataQueryResponse> | null = null;
        // When Odata next link exists then query by URL

        if (nextPageToken && nextPageToken.token) {
            responsePromise = this.queryODataByUrl(nextPageToken.token);
        } else {
            responsePromise = this._queryDefaultDetailedList_TestResultDailyTable(testResultContext);
        }

        const responseData: CommonTypes.IODataQueryResponse | null = await responsePromise;

        if (!responseData || !responseData.value) {
            return {} as CommonTypes.IDetailedListData;
        }

        // Iterate over all data.
        const detailedListItems = (responseData.value as any[]).map(d => {
            let detailedList = {
                itemkey: d.TestSK as string,
                itemName: d.Test.TestName as string,
                totalCount: d.TotalCount as number,
                passPercentage: d.PassRate as number,
                avgDuration: d.AvgDuration as number,
                failedCount: d.FailedCount as number,
                passedCount: d.PassedCount as number,
                inconclusiveCount: d.InconclusiveCount as number,
                abortedCount: d.AbortedCount as number,
                notExecutedCount: d.NotExecutedCount as number,
                errorCount: d.ErrorCount as number,
                notImpactedCount: d.NotImpactedCount as number
            } as CommonTypes.IDetailedListItem;

            if(isShowFlakyTestsDetailsEnabled(this.pageContext)){
                detailedList.flakyCount = d.FlakyCount as number;
            }
            return detailedList;
        });
        // to do correct the URl to use the const
        const prev = 0;
        // if (nextPageToken && nextPageToken.skip) {
        //     prev = Number(nextPageToken.skip);
        // }
        const itemCount = detailedListItems.length + prev;
        const defaultConfValues: CommonTypes.IReportConfiguration = {
            groupBy: CommonTypes.GroupBy.None,
            trendBy: CommonTypes.TrendBy.Days,
            period: CommonTypes.Period.Days_14,
            outcomes: [CommonTypes.TestOutcome.Failed],
            configuredFilters: {
                [CommonTypes.Filter.Workflow.toString()]: {
                    values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
                }
            }
        };
        return {
            detailedListItems: detailedListItems,
            confValues: defaultConfValues,
            isCachedData: responseData.isCachedData,
            nextPageToken: { token: responseData ? responseData["@odata.nextLink"] : undefined, skip: "" + itemCount }
        } as CommonTypes.IDetailedListData;
    }

    protected _queryDefaultDetailedList_TestResultDailyTable(
        testResultContext: CommonTypes.ITestReportContext
    ): Promise<CommonTypes.IODataQueryResponse> | null {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            const filterStr: string =
                `filter(${this._getTestResultsContextPipelineFilter(testResultContext)} and ${this._getDailyDateFilter(
                    CommonTypes.TrendBy.Days,
                    CommonTypes.Period.Days_14
                )}` +
                `  ${this._getFilterByFieldsString({
                    [CommonTypes.Filter.Workflow.toString()]: {
                        values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
                    }
                })}` +
                `)`;

            const outcomeAggrStr: string =
                `${this._getOutcomeCountAggregationStringTestRun(CommonTypes.TestOutcome.Passed)}, ${this._getOutcomeCountAggregationStringTestRun(
                    CommonTypes.TestOutcome.Failed
                )},` +
                `${this._getOutcomeCountAggregationStringTestRun(
                    CommonTypes.TestOutcome.NotExecuted
                )}, ${this._getOutcomeCountAggregationStringTestRun(CommonTypes.TestOutcome.NotImpacted)}`;

            let aggrStr = outcomeAggrStr;
            if(isShowFlakyTestsDetailsEnabled(this.pageContext)){
                aggrStr = aggrStr + `, ${ this._getFlakyCountAggregationString() }`
            }

            const groupByStr = `groupby((TestSK, Test/TestName), aggregate(ResultCount with sum as TotalCount, ResultDurationSeconds with sum as TotalDuration, ${aggrStr}))`;

            const $apply = `${filterStr}/${groupByStr}/filter(FailedCount gt 0)`;

            /* Here AvgDuration of test is calculated by sum of duration/sum of count.  Other way is find avg of each row in TestResultDaily and then take avg of all averages. */
            queryOptions.$apply =
                `${$apply}/compute(TotalDuration div cast(TotalCount,Edm.Double) as AvgDuration,` +
                `iif(TotalCount gt NotExecutedCount, ((PassedCount add NotImpactedCount) div cast(TotalCount sub NotExecutedCount, Edm.Decimal)) mul 100, 0) as PassRate)`;

            queryOptions.$orderby = "FailedCount desc";

            return this.queryOData(queryOptions, true);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _getOutcomeCountAggregationStringTestRun(outcome: CommonTypes.TestOutcome): string {
        switch (outcome) {
            case CommonTypes.TestOutcome.Failed:
                return `ResultFailCount with sum as FailedCount`;
            case CommonTypes.TestOutcome.Passed:
                return `ResultPassCount with sum as PassedCount`;
            case CommonTypes.TestOutcome.Aborted:
                return `ResultAbortedCount with sum as AbortedCount`;
            case CommonTypes.TestOutcome.Error:
                return `ResultErrorCount with sum as ErrorCount`;
            case CommonTypes.TestOutcome.Inconclusive:
                return `ResultInconclusiveCount with sum as InconclusiveCount`;
            case CommonTypes.TestOutcome.NotExecuted:
                return `ResultNotExecutedCount with sum as NotExecutedCount`;
            case CommonTypes.TestOutcome.NotImpacted:
                return `ResultNotImpactedCount with sum as NotImpactedCount`;
        }

        return "";
    }
}
